import { useForm } from 'react-hook-form';

import { getUrlServiceProtocolPDFDownload } from 'api/ApiUrls';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';
import { getClientTimeInISO } from 'library/utils/dateTime/clientTime';
import { usePdfDownloadSubmit } from 'modules/Shared/PdfPrint/usePdfDownloadSubmit';

import useStorePdfServiceProtocolPrintoutSettings from '../Hooks/useStorePdfPrintoutSettings';
import {
	PrintProtocolFormType,
	PrintProtocolUserSettings,
	mapFormToPdfRequest,
} from './Types';

export const usePdfFormSubmit = ({
	isDealer,
	protocolId,
	chassisSeries,
	chassisNumber,
	mapFormToRequest,
	defaultValues,
}: {
	isDealer: boolean;
	protocolId: number;
	chassisSeries: string;
	chassisNumber: number;
	mapFormToRequest: (
		protocolId: number,
		form: PrintProtocolFormType
	) => ReturnType<typeof mapFormToPdfRequest>;
	defaultValues?: PrintProtocolUserSettings;
}) => {
	const { trackFormSubmitSuccess } = useAdobeTracking();

	const updatePdfSettings = useStorePdfServiceProtocolPrintoutSettings();

	const deafultFormValues = {
		additionalText: '',
		printQr: true,
		printEstimatedHours: true,
		printUsedHours: true,
		printConfiguration: true,
		printEndCustomer: true,
		printCampaigns: true,
		printCoverage: true,
		printProposals: true,
		printServiceActivitiesPhotos: true,
		printAdditionalRepairs: true,
		printAdditionalPhotos: true,
		printSignatures: true,
	};

	const userSettingValues = { additionalText: '', ...defaultValues };

	const { handleSubmit, control, getValues } = useForm<PrintProtocolFormType>(
		{
			mode: 'onChange',
			defaultValues: defaultValues
				? userSettingValues
				: deafultFormValues,
		}
	);

	const getFileName = () =>
		`Service Protocol ${chassisSeries}${chassisNumber} ${getClientTimeInISO()}.pdf`;

	const { submit, isPending } = usePdfDownloadSubmit<PrintProtocolFormType>({
		url: getUrlServiceProtocolPDFDownload,
		getFileName,
		buildFormData: (data) => mapFormToRequest(protocolId, data),
		onSuccess: () => {
			void trackFormSubmitSuccess({
				type: 'service-protocol',
				name: 'Print Service Protocol',
				id: protocolId.toString(),
				destination: 'pdf-download',
				topic: isDealer ? 'dealer' : 'customer',
			});
		},
	});

	const onSubmit = handleSubmit((data) => {
		void updatePdfSettings(data, isDealer);
		void submit(data);
	});

	return { onSubmit, control, isPending, getValues };
};
